const BASE_URL = "https://api.calvant.com/framework/api/frameworks";
const CUSTOM_BASE_URL = "https://api.calvant.com/framework/api/custom-frameworks";
const ORG_BASE_URL = "https://api.calvant.com/user-service/api/organizations";

const fetchOrgFrameworks = async (organizationId, token) => {
  const res = await fetch(`${ORG_BASE_URL}/${organizationId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });
  if (!res.ok) throw new Error(`Failed to fetch organization: ${organizationId}`);
  const org = await res.json();
  // Older org documents may hold a custom framework as "CUSTOM:<id>"; the framework service
  // normalises that to "CUSTOM_<id>", so do the same here or the sidebar filter drops it.
  return Array.isArray(org.frameworks)
    ? org.frameworks.map((code) => code.replace(/^CUSTOM:/i, "CUSTOM_").toUpperCase())
    : [];
};

// The org's ACTIVE custom frameworks. Read-only endpoint, open to any member of the org.
// Never throws: a failure here must not take the whole framework list down.
const fetchOrgCustomFrameworks = async (organizationId, token, region) => {
  try {
    const headers = { Authorization: `Bearer ${token}` };
    if (region && region !== "AUTO") headers["x-region"] = region;
    const res = await fetch(`${CUSTOM_BASE_URL}/org/${organizationId}/active`, { headers });
    if (!res.ok) return [];
    const list = await res.json();
    return Array.isArray(list)
      ? list.map((fw) => ({
          code: fw.code,
          label: fw.label || fw.name,
          color: fw.color,
          path: fw.path,
          sub: fw.sub,
          type: fw.type,
          description: fw.description || fw.name,
          // Risk, Saved Risks, Integrations and the dashboard all key off these four fields.
          // They used to be dropped here, so every custom framework looked like it had no
          // risk types and no mapping sources.
          riskTypes: Array.isArray(fw.riskTypes) && fw.riskTypes.length ? fw.riskTypes : ["Security"],
          annexSectionTypes: fw.annexSectionTypes ?? [],
          isMapped: fw.isMapped ?? false,
          mappingSources: fw.mappingSources ?? [],
          custom: true,
        }))
      : [];
  } catch {
    return [];
  }
};

export const fetchFrameworks = async () => {
  let user;
  try {
    user = JSON.parse(sessionStorage.getItem("user"));
  } catch {
    throw new Error("[frameworkService] Failed to parse user from sessionStorage");
  }

  const organizationId = user?.organization;
  const token =
    typeof user?.token === "string"
      ? user.token
      : user?.token?.token ?? sessionStorage.getItem("token");

  if (!organizationId) throw new Error("[frameworkService] user.organization missing");
  if (!token) throw new Error("[frameworkService] No token found");

  const [catalogFrameworks, orgFrameworkCodes, customFrameworks] = await Promise.all([
    fetch(BASE_URL, {
      headers: { Authorization: `Bearer ${token}` },
    }).then((res) => {
      if (!res.ok) throw new Error("Failed to fetch frameworks");
      return res.json();
    }),
    fetchOrgFrameworks(organizationId, token),
    fetchOrgCustomFrameworks(organizationId, token, user?.region),
  ]);

  // /api/frameworks is the system catalog only (it never leaks another org's custom
  // frameworks), so the org's own published custom frameworks are merged in here. They only
  // count if the org's frameworks[] array also holds their code — that array is what billing
  // slots are counted against, so a framework outside it must never show up in the sidebar.
  const allFrameworks = [...catalogFrameworks, ...customFrameworks];

  return allFrameworks
    .filter((fw) => orgFrameworkCodes.includes(fw.code?.toUpperCase()))
    .map((fw) => {
      if (!fw.label || !fw.path) {
        console.warn(`[frameworkService] Incomplete config for framework: "${fw.code}"`);
        return null;
      }
      return {
        id: fw.label,
        code: fw.code,
        label: fw.label,
        color: fw.color,
        path: fw.path,
        description: fw.description,
        sub: fw.sub,
        type: fw.type,
        custom: !!fw.custom,
        riskTypes: fw.riskTypes ?? [],
        annexSectionTypes: fw.annexSectionTypes ?? [],
        isMapped: fw.isMapped ?? false,
        mappingSources: fw.mappingSources ?? [],
      };
    })
    .filter(Boolean);
};